import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Volume2, VolumeX, RefreshCw, Play, Pause, Maximize2, AlertTriangle, Wifi } from 'lucide-react';
import Hls from 'hls.js';
import api from '../services/api';
import LoadingShimmer from './LoadingShimmer';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import useStreamManager from '../hooks/useStreamManager';

/**
 * Optimized camera stream with adaptive quality and smart loading
 */
const OptimizedCameraStream = memo(({ 
  camera, 
  onStreamReady, 
  onStreamError,
  size = 'medium', // 'small', 'medium', 'large', 'fullscreen'
  autoStart = true,
  priority = 0,
  adaptiveQuality = true
}) => {
  const [isMuted, setIsMuted] = useState(true);
  const [error, setError] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreamStarted, setIsStreamStarted] = useState(false);
  const [currentQuality, setCurrentQuality] = useState(-1); // -1 = auto
  const [availableQualities, setAvailableQualities] = useState([]);
  const [bandwidth, setBandwidth] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [viewerCount, setViewerCount] = useState(0);
  const [sharingEfficiency, setSharingEfficiency] = useState(0);
  
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const bandwidthMonitorRef = useRef(null);

  // Stream manager for connection pooling and sharing
  const streamManager = useStreamManager();

  // Intersection observer for visibility-based loading
  const [containerRef, isIntersecting, intersectionRatio] = useIntersectionObserver({
    threshold: [0, 0.1, 0.5, 0.8],
    rootMargin: '50px'
  }, 200);

  // Update visibility state
  useEffect(() => {
    setIsVisible(isIntersecting && intersectionRatio > 0.1);
  }, [isIntersecting, intersectionRatio]);

  // Adaptive quality settings based on size and visibility
  const getOptimalQualitySettings = useCallback(() => {
    const sizeMultiplier = {
      small: 0.5,
      medium: 0.7,
      large: 1.0,
      fullscreen: 1.2
    }[size] || 0.7;

    const visibilityMultiplier = intersectionRatio > 0.8 ? 1.0 : 0.6;
    const connectionQuality = navigator.connection?.effectiveType || '4g';
    
    let qualityMultiplier = 1.0;
    switch (connectionQuality) {
      case 'slow-2g': qualityMultiplier = 0.3; break;
      case '2g': qualityMultiplier = 0.4; break;
      case '3g': qualityMultiplier = 0.6; break;
      case '4g': qualityMultiplier = 1.0; break;
    }

    const targetBandwidth = Math.floor(4000000 * sizeMultiplier * visibilityMultiplier * qualityMultiplier);
    
    return {
      maxBufferLength: size === 'fullscreen' ? 40 : 15,
      maxBufferSize: targetBandwidth * (size === 'fullscreen' ? 10 : 5),
      abrEwmaDefaultEstimate: targetBandwidth,
      enableWorker: size !== 'small',
      lowLatencyMode: size === 'fullscreen'
    };
  }, [size, intersectionRatio]);

  // Initialize HLS with adaptive settings
  const initializeVideoPlayer = useCallback(() => {
    if (!videoRef.current || !streamUrl || !isVisible) return;

    const absUrl = getAbsoluteStreamUrl(streamUrl);
    const settings = getOptimalQualitySettings();
    
    console.log(`🎥 Initializing stream for ${camera.name} with quality settings:`, settings);

    if (hlsRef.current) {
      hlsRef.current.destroy();
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        debug: process.env.NODE_ENV === 'development',
        enableWorker: settings.enableWorker,
        lowLatencyMode: settings.lowLatencyMode,
        
        // Adaptive buffer settings
        maxBufferSize: settings.maxBufferSize,
        maxBufferLength: settings.maxBufferLength,
        maxMaxBufferLength: settings.maxBufferLength * 2,
        maxBufferHole: 0.5,
        
        // Quality control
        abrEwmaDefaultEstimate: settings.abrEwmaDefaultEstimate,
        abrBandWidthFactor: adaptiveQuality ? 0.95 : 1.0,
        abrBandWidthUpFactor: adaptiveQuality ? 0.7 : 1.0,
        abrMaxWithRealBitrate: adaptiveQuality,
        
        // Start with appropriate level
        startLevel: adaptiveQuality ? -1 : (size === 'small' ? 2 : 0),
        capLevelToPlayerSize: adaptiveQuality && size !== 'fullscreen',
        capLevelOnFPSDrop: adaptiveQuality,
        
        // Error handling
        fragLoadingMaxRetry: 3,
        manifestLoadingMaxRetry: 3,
        levelLoadingMaxRetry: 3,
        fragLoadingTimeOut: 20000,
        manifestLoadingTimeOut: 10000,
        levelLoadingTimeOut: 10000
      });

      hlsRef.current = hls;
      
      // Register stream with sharing integration
      streamManager.registerStream(camera._id, { 
        hls, 
        url: absUrl, 
        quality: currentQuality,
        type: 'live',
        userId: 'current_user' // TODO: Get from auth context
      });

      hls.loadSource(absUrl);
      hls.attachMedia(videoRef.current);

      // Event handlers
      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        console.log(`📋 Manifest parsed for ${camera.name}, ${data.levels.length} quality levels available`);
        setAvailableQualities(data.levels.map((level, index) => ({
          index,
          height: level.height,
          bitrate: Math.round(level.bitrate / 1000)
        })));
        
        videoRef.current.play().catch(error => {
          console.log('Autoplay prevented:', error);
        });
      });

      hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
        setCurrentQuality(data.level);
        console.log(`🎯 Quality switched to level ${data.level} for ${camera.name}`);
      });

      hls.on(Hls.Events.FRAG_LOADING, () => {
        // Monitor bandwidth
        if (bandwidthMonitorRef.current) {
          clearTimeout(bandwidthMonitorRef.current);
        }
        
        bandwidthMonitorRef.current = setTimeout(() => {
          if (hls.bandwidthEstimate) {
            setBandwidth(Math.round(hls.bandwidthEstimate / 1000));
          }
        }, 1000);
      });

      // Ready event
      videoRef.current.addEventListener('playing', () => {
        console.log(`▶️ Stream playing for ${camera.name}`);
        setIsLoading(false);
        setError(null);
        setRetryAttempts(0);
        
        // Update viewer count and sharing efficiency
        const currentViewerCount = streamManager.getViewerCount(camera._id);
        const efficiency = streamManager.getSharingEfficiency();
        setViewerCount(currentViewerCount);
        setSharingEfficiency(efficiency.efficiency);
        
        onStreamReady?.(camera._id);
      });

      // Error handling
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error(`❌ HLS Error for ${camera.name}:`, data);
        
        if (data.fatal) {
          handleStreamError(data);
        } else {
          // Non-fatal errors - just log them
          console.warn(`⚠️ Non-fatal HLS error for ${camera.name}:`, data.details);
        }
      });

      videoRef.current.onerror = (error) => {
        console.error(`🔴 Video error for ${camera.name}:`, error);
        handleStreamError({ type: 'mediaError', details: 'videoError' });
      };

    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      videoRef.current.src = absUrl;
      videoRef.current.addEventListener('playing', () => {
        console.log(`▶️ Stream playing (Safari) for ${camera.name}`);
        setIsLoading(false);
        setError(null);
        setRetryAttempts(0);
        onStreamReady?.(camera._id);
      });
      
      videoRef.current.play().catch(error => {
        console.log('Safari autoplay prevented:', error);
      });
    } else {
      setError('Browser not supported');
    }
  }, [streamUrl, isVisible, camera, getOptimalQualitySettings, adaptiveQuality, currentQuality, onStreamReady, size, streamManager]);

  // Handle stream errors with smart retry
  const handleStreamError = useCallback((errorData) => {
    const shouldRetry = retryAttempts < 3 && camera.isOnline;
    
    if (shouldRetry) {
      const retryDelay = Math.min(5000 * Math.pow(2, retryAttempts), 30000); // Exponential backoff
      console.log(`🔄 Retrying stream for ${camera.name} in ${retryDelay}ms (attempt ${retryAttempts + 1})`);
      
      setRetryAttempts(prev => prev + 1);
      setError(`Reconnecting... (${retryAttempts + 1}/3)`);
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      
      retryTimeoutRef.current = setTimeout(() => {
        startStream();
      }, retryDelay);
    } else {
      console.error(`🚫 Max retries exceeded for ${camera.name}`);
      setError('Connection failed');
      setIsLoading(false);
      onStreamError?.(camera._id, errorData);
    }
  }, [retryAttempts, camera, onStreamError]);

  // Start stream with priority management
  const startStream = useCallback(async () => {
    if (!camera || !isVisible || streamManager.isStreamLoading(camera._id)) {
      return;
    }

    // Check if stream already exists
    if (streamManager.isStreamActive(camera._id)) {
      const existingStream = streamManager.getStream(camera._id);
      if (existingStream && existingStream.url) {
        setStreamUrl(existingStream.url);
        setIsStreamStarted(true);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    streamManager.markLoading(camera._id);

    const loadStream = async () => {
      try {
        console.log(`🚀 Starting stream for ${camera.name} (priority: ${priority})`);
        const res = await api.get(`/cameras/${camera._id}/stream`, {
          timeout: 15000
        });

        if (res.data.data.streamUrl) {
          setStreamUrl(res.data.data.streamUrl);
          setIsStreamStarted(true);
          console.log(`✅ Stream URL set for ${camera.name}`);
        } else {
          throw new Error('No stream URL received');
        }
      } catch (error) {
        console.error(`❌ Stream start error for ${camera.name}:`, error);
        handleStreamError({ type: 'networkError', details: 'streamStartError' });
      }
    };

    // Use stream manager for priority-based loading
    streamManager.requestStream(camera._id, priority, loadStream);
  }, [camera, isVisible, priority, streamManager, handleStreamError]);

  // Auto-start effect
  useEffect(() => {
    if (autoStart && isVisible && !isStreamStarted && camera?.isOnline) {
      const delay = priority > 5 ? 100 : 500 + (Math.random() * 1000); // Stagger requests
      const timer = setTimeout(startStream, delay);
      return () => clearTimeout(timer);
    }
  }, [autoStart, isVisible, isStreamStarted, camera?.isOnline, startStream, priority]);

  // Initialize player when stream URL is ready
  useEffect(() => {
    if (streamUrl && isVisible) {
      const timer = setTimeout(initializeVideoPlayer, 100);
      return () => clearTimeout(timer);
    }
  }, [streamUrl, isVisible, initializeVideoPlayer]);

  // Pause/resume based on visibility
  useEffect(() => {
    if (videoRef.current && isStreamStarted) {
      if (isVisible && intersectionRatio > 0.1) {
        videoRef.current.play().catch(console.log);
      } else if (intersectionRatio < 0.05) {
        videoRef.current.pause();
      }
    }
  }, [isVisible, intersectionRatio, isStreamStarted]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (bandwidthMonitorRef.current) {
        clearTimeout(bandwidthMonitorRef.current);
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      streamManager.releaseStream(camera._id, false); // Delayed cleanup
    };
  }, [camera._id, streamManager]);

  const getAbsoluteStreamUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const baseUrl = import.meta.env.VITE_API_STREAM_URL || 
                   import.meta.env.VITE_API_URL?.replace('/api', '') || 
                   'http://localhost:3667';
    return baseUrl + url;
  };

  const handleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const handleRetry = useCallback(() => {
    console.log(`🔄 Manual retry for ${camera.name}`);
    setRetryAttempts(0);
    setError(null);
    setStreamUrl(null);
    setIsStreamStarted(false);
    
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    
    streamManager.releaseStream(camera._id, true); // Immediate cleanup
    startStream();
  }, [camera, startStream, streamManager]);

  const handleQualityChange = useCallback((levelIndex) => {
    if (hlsRef.current && adaptiveQuality) {
      hlsRef.current.currentLevel = levelIndex;
      hlsRef.current.autoLevelEnabled = levelIndex === -1;
      setCurrentQuality(levelIndex);
      console.log(`🎯 Manual quality change to level ${levelIndex} for ${camera.name}`);
    }
  }, [adaptiveQuality, camera]);

  if (!camera) {
    return (
      <div className="bg-black rounded overflow-hidden h-full flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl mb-2">📹</div>
          <p>No camera</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative bg-black h-full rounded overflow-hidden">
      {/* Controls Overlay */}
      <div className="absolute top-2 right-2 flex space-x-1 z-20">
        {/* Mute Button */}
        <button
          onClick={handleMute}
          className="bg-black bg-opacity-50 text-white p-1 rounded hover:bg-opacity-70 transition-colors"
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
        </button>

        {/* Retry Button */}
        <button
          onClick={handleRetry}
          disabled={isLoading}
          className="bg-black bg-opacity-50 text-white p-1 rounded hover:bg-opacity-70 transition-colors disabled:opacity-50"
          title="Refresh Stream"
        >
          <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Quality Selector (Development/Debug) */}
      {process.env.NODE_ENV === 'development' && adaptiveQuality && availableQualities.length > 0 && (
        <div className="absolute top-2 left-2 z-20">
          <select
            value={currentQuality}
            onChange={(e) => handleQualityChange(parseInt(e.target.value))}
            className="bg-black bg-opacity-50 text-white text-xs p-1 rounded"
          >
            <option value={-1}>Auto ({bandwidth}k)</option>
            {availableQualities.map(quality => (
              <option key={quality.index} value={quality.index}>
                {quality.height}p ({quality.bitrate}k)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Status Indicators */}
      <div className="absolute bottom-2 left-2 flex items-center space-x-1 z-20">
        {/* Connection Status */}
        <div className={`w-2 h-2 rounded-full ${camera.isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
        
        {/* Stream Status */}
        {isStreamStarted && !error && (
          <Wifi className="h-3 w-3 text-green-500" />
        )}
        
        {/* Viewer Count */}
        {viewerCount > 1 && (
          <div className="bg-black bg-opacity-50 text-white text-xs px-1.5 py-0.5 rounded flex items-center space-x-1">
            <Users className="h-2.5 w-2.5" />
            <span>{viewerCount}</span>
          </div>
        )}
      </div>

      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-full h-full bg-black object-cover"
        autoPlay
        muted={isMuted}
        playsInline
        style={{ 
          display: streamUrl && !error ? 'block' : 'none',
          imageRendering: size === 'small' ? 'pixelated' : 'auto'
        }}
      />

      {/* Loading/Error Overlay */}
      {(isLoading || error || !streamUrl || !isVisible) && (
        <div className="absolute inset-0 bg-black">
          {!isVisible ? (
            <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <div className="text-4xl mb-2">📹</div>
                <p className="text-sm">Camera Ready</p>
              </div>
            </div>
          ) : error ? (
            <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
              <div className="text-center text-white">
                <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm mb-2">{error}</p>
                {retryAttempts < 3 && (
                  <button 
                    onClick={handleRetry}
                    className="text-xs bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded transition-colors"
                  >
                    Retry Now
                  </button>
                )}
              </div>
            </div>
          ) : (
            <LoadingShimmer />
          )}
        </div>
      )}

      {/* Development Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute bottom-2 right-2 text-xs bg-black bg-opacity-50 text-white px-2 py-1 rounded">
          Visible: {intersectionRatio.toFixed(2)} | Quality: {currentQuality} | BW: {bandwidth}k | Viewers: {viewerCount} | Efficiency: {sharingEfficiency.toFixed(1)}x
        </div>
      )}
    </div>
  );
});

OptimizedCameraStream.displayName = 'OptimizedCameraStream';

export default OptimizedCameraStream;